import _ from 'lodash';
import UriJs from 'urijs';
import states from 'states-us';
import getAxiosInstance, {BASE_URL_TYPE} from '../api/axios';
import utilities from './utilities';
import {getActualPublicToken} from './utilitiesToken';
import {
  fitBounds, leftPanelWidth, frameUsOverview,
} from '../Components/Containers/Map/Elements/helpers/common';
import {stripPropertyQueryParams} from '../helpers/common';
import refreshMetatags from '../Components/Elements/MetaTags';
import {ALLOWED_PLACE_TYPES} from '../Components/Containers/Sitemap/constValues';

const axios = getAxiosInstance('', true);
const boundaryLayerName = 'place_state_boundary';
const boundarySourceName = 'place_boundary_source';
const fillColor = '#1d6ff2';
const fillColorZoningData = '#30ab00';
const lineColorParent = '#fe0000';
const lineColor = '#0c2a91';
// Identity-based fit dedupe: the map is fitted at most once per "currently fitted target".
// Duplicate auto-fits for the same place (e.g. openPlace fitting, then the Map.js navigation
// effect reacting to the same navigation and fitting again) collapse to a single fit, with no
// wall-clock window — so behavior is identical across fast and slow environments. The previous
// timer-based approach leaked duplicates through on slow envs and wrongly suppressed legitimate
// re-fits on fast ones. resetNavigationFit() clears the target after a real user map move so an
// intentional re-fit of the same place (e.g. recenter via breadcrumb after panning away) still
// runs.
let lastFittedKey = '';
let suppressNextNavigationFit = false;

export const resetNavigationFit = () => {
  lastFittedKey = '';
  // A real user map move means future fits should be honoured — also drop any pending one-shot
  // suppression so it can never leak onto an unrelated, later navigation.
  suppressNextNavigationFit = false;
};

// Suppress the next navigation-driven camera fit (one-shot). Used when leaving a short property URL
// for its market on deselect: the market path change would otherwise re-fit to the market bounds and
// zoom the user out, but we want to keep the current view on the just-drawn search shape (matching
// the long-URL behaviour, which never changes the zoom).
export const suppressNavigationFit = () => {
  suppressNextNavigationFit = true;
};

export const GRID_PLACE_FILL_OPACITY = 0.4;
export const GRID_PLACE_HOVER_OPACITY = 0.5;
const canUseStyle = (map) => {
  if (!map || !map.getStyle || !map.isStyleLoaded) {
    return false;
  }
  try {
    return !!map.isStyleLoaded();
  }
  catch (error) {
    console.warn('Skip map style operation while style load state is unavailable', error);
    return false;
  }
};
const hasLayer = (map, layerId) => canUseStyle(map) && !!map.getLayer(layerId);
const getNavigationFitKey = (featureProperties = {}) => {
  const id = featureProperties.placeId || featureProperties.id || featureProperties.geoid || featureProperties.fips || '';
  const extent = featureProperties.extent || '';
  if (!id || !extent) {
    return '';
  }
  return `${id}:${extent}`;
};

const shouldSkipNavigationFit = (key = '') => {
  if (suppressNextNavigationFit) {
    suppressNextNavigationFit = false;
    if (key) {
      lastFittedKey = key;
    }
    return true;
  }
  if (!key) {
    return false;
  }
  if (lastFittedKey === key) {
    return true;
  }
  lastFittedKey = key;
  return false;
};

export const placeTypes = {
  State: ['County'],
  County: [
    'Zona Urbana',
    'Balance',
    'Municipality,Borough',
    'City,Municipality',
    // 'Undefined',
    'Town',
    'Comunidad',
    'Village',
    'Municipality,Undefined',
    // 'Community Development District',
    'Town,Municipality',
    'Borough',
    'Municipality',
    'City',
    'Census Designated Place',
    'Village,Municipality',
    'Unincorporated',
    // 'Target Urban Area',
    // 'Target Urban Area Corridor',
    // 'Township',
    // 'Combined Statistical Area',
    // 'Neighborhood',
    // 'Commission District',
    // 'Neighborhood Revitalization Area',
    // 'Brownfield',
    // 'Community Redevelopment Area',
    // 'Metropolitan Division',
    // 'Subdivision',
    // 'NET Area',
  ],
};

export const COUNTY_SUBDIVISION_PLACE_TYPES = [
  'County Subdivision',
  'County Subdivision,Township',
  'County Subdivision,Charter Township',
  'County Subdivision,Town',
  'County Subdivision,Borough',
  'County Subdivision,City',
  'County Subdivision,District',
  'County Subdivision,Plantation',
  'County Subdivision,Reservation',
  'County Subdivision,County',
  'County Subdivision,village',
];

export const COUNTY_SUBDIVISION_LAYER_ID = 'place_county_subdivision';
export const COUNTY_SUBDIVISION_LINE_LAYER_ID = 'place_county_subdivision_line';

const ensureCountySubdivisionLayers = (map) => {
  if (!canUseStyle(map)) {
    return;
  }
  if (!map.getSource('place_state')) {
    return;
  }

  // Fill layer (inserted under place_state)
  if (!map.getLayer(COUNTY_SUBDIVISION_LAYER_ID)) {
    let beforeId;
    if (map.getLayer('place_state')) {
      beforeId = 'place_state';
    }
    else if (map.getLayer('place_state_line')) {
      beforeId = 'place_state_line';
    }
    map.addLayer(
      {
        id: COUNTY_SUBDIVISION_LAYER_ID,
        type: 'fill',
        source: 'place_state',
        'source-layer': 'place_state',
        layout: {visibility: 'none'},
        paint: {
          'fill-color': fillColor,
          'fill-opacity': GRID_PLACE_FILL_OPACITY,
        },
        filter: ['all', ['in', 'place_type', ...COUNTY_SUBDIVISION_PLACE_TYPES]],
      },
      beforeId,
    );
  }

  // Line layer (inserted under place_state_line)
  if (!map.getLayer(COUNTY_SUBDIVISION_LINE_LAYER_ID)) {
    let beforeId;
    if (map.getLayer('place_state_line')) {
      beforeId = 'place_state_line';
    }
    else if (map.getLayer('place_state')) {
      beforeId = 'place_state';
    }
    map.addLayer(
      {
        id: COUNTY_SUBDIVISION_LINE_LAYER_ID,
        type: 'line',
        source: 'place_state',
        'source-layer': 'place_state',
        layout: {visibility: 'none'},
        paint: {
          'line-color': lineColor,
          'line-width': 0.75,
        },
        filter: ['all', ['in', 'place_type', ...COUNTY_SUBDIVISION_PLACE_TYPES], ['==', 'generate_unincorporated', 1]],
      },
      beforeId,
    );
  }
};

export const hidePlaceGridLayers = (map) => {
  if (!canUseStyle(map)) {
    return;
  }
  [
    {id: 'place_state', opacityProperty: 'fill-opacity'},
    {id: 'place_state_line'},
    {id: COUNTY_SUBDIVISION_LAYER_ID, opacityProperty: 'fill-opacity'},
    {id: COUNTY_SUBDIVISION_LINE_LAYER_ID},
  ].forEach(({id, opacityProperty}) => {
    if (!map.getLayer(id)) {
      return;
    }
    if (opacityProperty) {
      map.setPaintProperty(id, opacityProperty, 0);
    }
    map.setLayoutProperty(id, 'visibility', 'none');
  });
};

export const hideBoundaryLayer = (map) => {
  if (!canUseStyle(map)) {
    return;
  }
  if (map.getLayer(boundaryLayerName)) {
    map.setLayoutProperty(boundaryLayerName, 'visibility', 'none');
  }
  if (map.getLayer('place_state_line')) {
    map.setLayoutProperty('place_state_line', 'visibility', 'none');
  }
};

export const clearPropertyBoundaryLayers = (map) => {
  if (!canUseStyle(map) || !map.getStyle) {
    return;
  }
  let layers = [];
  try {
    layers = map.getStyle()?.layers || [];
  }
  catch (error) {
    console.warn('Skip removing property boundary layers during style transition', error);
    return;
  }
  layers
    .map(layer => layer?.id)
    .filter(layerId => typeof layerId === 'string' && layerId.includes('property-layer'))
    .forEach((layerId) => {
      if (map.getLayer(layerId)) {
        map.removeLayer(layerId);
      }
    });
};

export const zoningPlacesPaint = (placeType, placesSettings) => {
  let uniqueFilterValues = [];
  let filterName = '';
  switch (placeType) {
    case 'State':
      uniqueFilterValues = [...new Set(_.map(placesSettings, obj => obj.state))];
      filterName = 'state';
      break;

    case 'County':
      uniqueFilterValues = [...new Set(_.map(placesSettings, obj => obj?.fipsCode))].filter(fipsCode => !!fipsCode);
      filterName = 'fips';
      break;

    default:
      uniqueFilterValues = Object.keys(placesSettings);
      filterName = 'id';
      break;
  }
  return _.transform(uniqueFilterValues, (paintStyles, filterValue) => {
    paintStyles.push(['==', ['get', filterName], filterValue]);
    paintStyles.push(fillColorZoningData);
  }).concat([fillColor]);
};

export const openCountry = (bounds, map, placesSettings, history = null) => {
  if (!canUseStyle(map)) {
    // When switching pages quickly the style can be mid-transition; retry once when ready.
    if (map?.once) {
      let fired = false;
      const runOnce = () => {
        if (fired) return;
        fired = true;
        openCountry(bounds, map, placesSettings, history);
      };
      map.once('styledata', runOnce);
      map.once('idle', runOnce);
    }
    return;
  }
  hideBoundaryLayer(map);
  [COUNTY_SUBDIVISION_LAYER_ID, COUNTY_SUBDIVISION_LINE_LAYER_ID].forEach((layerId) => {
    if (map.getLayer(layerId)) {
      map.setLayoutProperty(layerId, 'visibility', 'none');
    }
  });
  ['place_state', 'place_state_line'].forEach((layerId) => {
    map.setFilter(layerId, ['all', ['==', 'place_type', 'State']]);
    map.setLayoutProperty(layerId, 'visibility', 'visible');
  });
  map.setPaintProperty('place_state', 'fill-opacity', GRID_PLACE_FILL_OPACITY);
  map.setPaintProperty('place_state', 'fill-color', ['case'].concat(zoningPlacesPaint('State', placesSettings)));
  map.setPaintProperty('place_state_line', 'line-color', lineColor);
  map.setPaintProperty('place_state_line', 'line-width', 1);
  // Frame the whole US and re-apply the fit-then-lock fence, identical to map init, so returning to
  // the country level reproduces the exact same view (no shift). The passed `bounds` (full-US extent
  // from the breadcrumb) is intentionally not used — see US_OVERVIEW_BOUNDS rationale in helpers/common.js.
  frameUsOverview(map);
  if (history) {
    history.push('/country/us');
    localStorage.setItem('city-search', '/country/us');
    refreshMetatags(history.location.pathname);
  }
};

export const getPath = (path = '', search = '') => {
  if (!path) {
    return path;
  }

  if (path[0] === '/') {
    return `${path}${search}`;
  }

  return `/${path}${search}`;
};

const normalizeHistoryPath = (path = '') => {
  if (!path) {
    return '';
  }
  const normalized = path.toString().replace(/^\/+/, '');
  return getPath(normalized);
};

const normalizePlaceType = (raw) => {
  const v = Array.isArray(raw) ? raw[0] : raw;
  if (!v || typeof v !== 'string') {
    return '';
  }
  const t = v.trim();
  const lower = t.toLowerCase();
  if (lower === 'state') return 'State';
  if (lower === 'county') return 'County';
  return t;
};

const getParentBoundaryFilter = (placeBoundaryData = {}) => {
  const boundaryId = placeBoundaryData.placeId;
  const boundaryGeoid = (
    placeBoundaryData.geoid
    || placeBoundaryData.fipsCode
    || placeBoundaryData.fips
    || ''
  ).toString();
  const identityFilter = ['any', ['==', 'id', boundaryId]];
  if (boundaryGeoid) {
    identityFilter.push(['==', 'geoid', boundaryGeoid], ['==', 'fips', boundaryGeoid]);
  }
  const placeType = normalizePlaceType(placeBoundaryData.placeType);
  if (placeType === 'County') {
    return ['all', ['==', 'place_type', 'County'], identityFilter];
  }
  if (placeType === 'State') {
    return ['all', ['==', 'place_type', 'State'], identityFilter];
  }
  return identityFilter;
};

export const openPlace = (
  featureProperties,
  map,
  placesSettings,
  setPlaceBoundary,
  history,
  options = {},
) => {
  if (!featureProperties) {
    return;
  }

  const {
    fitKey = getNavigationFitKey(featureProperties),
    preserveSearch = true,
    keepPropertyParams = false,
  } = options;
  const styleReady = canUseStyle(map);
  const placeType = normalizePlaceType(featureProperties.place_type);
  const isParentPlace = ['State', 'County'].includes(placeType);
  const applyPlaceStyle = () => {
    if (!canUseStyle(map)) {
      return false;
    }
    ensureCountySubdivisionLayers(map);
    if (isParentPlace) {
      hideBoundaryLayer(map);
    }
    else if (map.getLayer('place_state_line')) {
      map.setLayoutProperty('place_state_line', 'visibility', 'none');
    }
    // Only State/County are "parent" map tiles. Defaulting missing place_type to State wrongly
    // kept the county grid visible after clicking a city from county view.
    if (map.getLayer('place_state_line')) {
      map.setLayoutProperty('place_state_line', 'visibility', isParentPlace ? 'visible' : 'none');
    }
    const placeTypeFilterValue = placeType === 'State'
      ? featureProperties.state
      : featureProperties.geoid || featureProperties.fipsCode || featureProperties.fips;
    const placeTypeFilterName = placeType === 'State' ? 'state' : 'fips';
    const childPlaceTypes = placeTypes[placeType] || [];
    ['place_state', 'place_state_line'].forEach((layerId) => {
      if (!map.getLayer(layerId)) {
        return;
      }
      if (!isParentPlace) {
        if (map.getLayer('place_state')) {
          map.setPaintProperty('place_state', 'fill-opacity', 0);
        }
        map.setLayoutProperty(layerId, 'visibility', 'none');
      }
      else {
        if (layerId === 'place_state' && map.getLayer('place_state')) {
          map.setPaintProperty('place_state', 'fill-opacity', 0.4);
          map.setLayoutProperty(layerId, 'visibility', 'visible');
          map.setPaintProperty(
            'place_state',
            'fill-color',
            ['case'].concat(zoningPlacesPaint(placeType === 'State' ? 'County' : 'Place', placesSettings)),
          );
        }
        // generate_unincorporated values:
        // 0 - Exclude place, add to unicorporated
        // 1 - Include place, remove from unicorporated
        // NULL - placesTypes - 1, nonPlacesTypes - 0
        map.setFilter(layerId, [
          'any',
          [
            'all',
            ['==', placeTypeFilterName, placeTypeFilterValue],
            ['in', 'place_type', ...childPlaceTypes],
            ['in', 'import_feed', 'census.gov'],
            ['!=', 'generate_unincorporated', 0],
          ],
          [
            'all',
            ['==', placeTypeFilterName, placeTypeFilterValue],
            ['==', 'generate_unincorporated', 1],
            ['in', 'place_type', ...childPlaceTypes],
          ],
        ]);
      }
    });

    // County subdivision layer: only on County view; keep it under place_state / place_state_line.
    [COUNTY_SUBDIVISION_LAYER_ID, COUNTY_SUBDIVISION_LINE_LAYER_ID].forEach((layerId) => {
      if (!map.getLayer(layerId)) {
        return;
      }
      const shouldShow = isParentPlace && placeType === 'County';
      map.setLayoutProperty(layerId, 'visibility', shouldShow ? 'visible' : 'none');
      if (!shouldShow) {
        return;
      }
      if (layerId === COUNTY_SUBDIVISION_LAYER_ID) {
        // When this layer was previously hidden, hover logic may have set fill-opacity to 0.
        // Ensure a consistent base opacity when re-enabling the overlay on County pages.
        map.setPaintProperty(COUNTY_SUBDIVISION_LAYER_ID, 'fill-opacity', GRID_PLACE_FILL_OPACITY);
        map.setPaintProperty(
          COUNTY_SUBDIVISION_LAYER_ID,
          'fill-color',
          ['case'].concat(zoningPlacesPaint('Place', placesSettings)),
        );
      }
      map.setFilter(layerId, [
        'any',
        [
          'all',
          ['==', placeTypeFilterName, placeTypeFilterValue],
          ['in', 'place_type', ...COUNTY_SUBDIVISION_PLACE_TYPES],
          ['in', 'import_feed', 'census.gov'],
          ['==', 'generate_unincorporated', 1],
        ],
      ]);
    });

    if (isParentPlace && map.getLayer(boundaryLayerName)) {
      const boundaryId = featureProperties.placeId || featureProperties.id;
      const boundaryGeoid = (
        featureProperties.geoid
        || featureProperties.fipsCode
        || featureProperties.fips
        || ''
      ).toString();
      const boundaryFilter = getParentBoundaryFilter({
        placeId: boundaryId,
        geoid: boundaryGeoid,
        placeType,
      });
      map.setLayoutProperty(boundaryLayerName, 'visibility', 'visible');
      map.setFilter(boundaryLayerName, boundaryFilter);
    }
    const extent = (featureProperties.extent || '').split(',');
    if (extent.length && extent[0] && !shouldSkipNavigationFit(fitKey)) {
      map.fitBounds(extent, {padding: 50, duration: 0});
    }
    return true;
  };

  const waitForStyleEvent = (callback) => {
    if (!map?.once) {
      callback();
      return;
    }
    let fired = false;
    const runOnce = () => {
      if (fired) {
        return;
      }
      fired = true;
      callback();
    };
    map.once('styledata', runOnce);
    map.once('idle', runOnce);
  };

  const retryApplyPlaceStyle = (attempt = 1) => {
    const applied = applyPlaceStyle();
    if (applied || attempt >= 5) {
      return;
    }
    waitForStyleEvent(() => retryApplyPlaceStyle(attempt + 1));
  };

  if (styleReady) {
    applyPlaceStyle();
  }
  else {
    waitForStyleEvent(() => retryApplyPlaceStyle(1));
  }
  if (history) {
    const placePath = normalizeHistoryPath(featureProperties.path || featureProperties.url || '');
    // Prefer the real browser URL search because setQueryParam(...) updates window.history
    // and react-router history.location.search can lag during cross-market transitions.
    const rawSearch = window.location.search || history.location?.search || '';
    // By default a place navigation must not carry a property folio: when entering a market without
    // a property (grid/breadcrumb click) window.location.search is often empty and the fallback to
    // history.location.search can be stale, still holding a previous property's folio. Callers that
    // are opening a property in a new market pass keepPropertyParams so the just-set folio survives.
    const currentSearch = keepPropertyParams ? rawSearch : stripPropertyQueryParams(rawSearch);
    const nextPath = placePath || '/country/us';
    // State/County (parent) pages must never carry leftover query params from a market page.
    const nextWithSearch = preserveSearch && !isParentPlace && currentSearch && !nextPath.includes('?')
      ? `${nextPath}${currentSearch}`
      : nextPath;
    const currentPath = window.location.pathname + window.location.search;
    if (currentPath !== nextWithSearch) {
      history.push(nextWithSearch);
    }
    localStorage.setItem('city-search', nextWithSearch);
    refreshMetatags(history.location.pathname);
  }
  setPlaceBoundary({
    placeId: featureProperties.placeId || featureProperties.id,
    placeType: featureProperties.place_type,
    geoid: featureProperties.geoid,
    fipsCode: featureProperties.fipsCode || featureProperties.fips,
    fips: featureProperties.fips || featureProperties.fipsCode,
    geometry: featureProperties?.gisData?.geom || featureProperties.geometry || null,
  });
};

export const setupBoundaryLayer = (map, placeBoundary) => {
  if (!canUseStyle(map)) {
    return;
  }
  const placeBoundaryData = typeof placeBoundary === 'object' ? placeBoundary : {placeId: placeBoundary};
  const geometry = placeBoundaryData?.geometry || placeBoundaryData?.gisData?.geom;
  const boundaryFilter = getParentBoundaryFilter(placeBoundaryData);
  const placeType = placeBoundaryData?.placeType;
  const isParentPlace = ['State', 'County'].includes(placeType);
  if (!isParentPlace && !geometry) {
    return;
  }

  if (isParentPlace) {
    if (!map.getSource('place_state')) {
      return;
    }
    if (map.getLayer(boundaryLayerName)) {
      map.removeLayer(boundaryLayerName);
    }
    map.addLayer({
      id: boundaryLayerName,
      type: 'line',
      source: 'place_state',
      'source-layer': 'place_state',
      paint: {
        'line-color': lineColorParent,
        'line-width': 3,
      },
      filter: boundaryFilter,
    });
    return;
  }

  const boundaryFeature = {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        id: placeBoundaryData.placeId,
        properties: {id: placeBoundaryData.placeId},
        geometry,
      },
    ],
  };
  if (!map.getSource(boundarySourceName)) {
    map.addSource(boundarySourceName, {
      type: 'geojson',
      data: boundaryFeature,
    });
  }
  if (map.getLayer(boundaryLayerName)) {
    map.removeLayer(boundaryLayerName);
  }
  map.addLayer({
    id: boundaryLayerName,
    type: 'line',
    source: boundarySourceName,
    paint: {
      'line-color': lineColorParent,
      'line-width': 3,
    },
  });
};

export const boundaryReset = (placeBoundary, map) => {
  if (!map) {
    return;
  }
  const placeBoundaryData = typeof placeBoundary === 'object' ? placeBoundary : {placeId: placeBoundary};
  if (placeBoundaryData?.placeId) {
    const boundaryFilter = getParentBoundaryFilter(placeBoundaryData);
    const geometry = placeBoundaryData?.geometry || placeBoundaryData?.gisData?.geom;
    const isParentPlace = ['State', 'County'].includes(placeBoundaryData?.placeType);
    if (!isParentPlace && !geometry) {
      hideBoundaryLayer(map);
      return;
    }
    const currentBoundaryLayer = map.getLayer(boundaryLayerName);
    const expectedSource = isParentPlace ? 'place_state' : boundarySourceName;
    const hasMismatchedSource = !!currentBoundaryLayer && currentBoundaryLayer.source !== expectedSource;
    if (hasMismatchedSource) {
      map.removeLayer(boundaryLayerName);
    }
    const boundaryFeature = isParentPlace
      ? null
      : {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            id: placeBoundaryData.placeId,
            properties: {id: placeBoundaryData.placeId},
            geometry,
          },
        ],
      };
    const retryOnIdle = () => {
      map.once('idle', () => {
        boundaryReset(placeBoundaryData, map);
      });
    };
    if (isParentPlace && !map.getSource('place_state')) {
      retryOnIdle();
      return;
    }
    if (map.getLayer(boundaryLayerName)) {
      map.setLayoutProperty(boundaryLayerName, 'visibility', 'visible');
      if (isParentPlace) {
        map.setFilter(boundaryLayerName, boundaryFilter);
      }
      else if (map.getSource(boundarySourceName)) {
        map.getSource(boundarySourceName).setData(boundaryFeature);
      }
      else {
        retryOnIdle();
      }
    }
    else if (map.isStyleLoaded()) {
      if (isParentPlace || geometry) {
        setupBoundaryLayer(map, placeBoundaryData);
        if (!map.getLayer(boundaryLayerName)) {
          retryOnIdle();
        }
      }
    }
    else {
      map.once('idle', () => {
        if (!map.getLayer(boundaryLayerName)) {
          if (isParentPlace || geometry) {
            setupBoundaryLayer(map, placeBoundaryData);
          }
        }
        else {
          map.setLayoutProperty(boundaryLayerName, 'visibility', 'visible');
          if (isParentPlace) {
            map.setFilter(boundaryLayerName, boundaryFilter);
          }
          else if (map.getSource(boundarySourceName)) {
            map.getSource(boundarySourceName).setData(boundaryFeature);
          }
        }
      });
    }
  }
};

export const createStateBreadcrumb = (feature = {}) => ({
  id: feature.geoid,
  type: 'state',
  placeId: feature.id,
  text: feature.title,
  place_type: feature.place_type,
  geoid: feature.geoid,
  extent: feature.extent,
  state: feature.state,
  path: feature.path,
});
export const createCountyBreadcrumb = (feature = {}) => ({
  id: feature.geoid,
  type: 'county',
  placeId: feature.id,
  text: feature.title,
  place_type: feature.place_type,
  geoid: feature.geoid,
  fipsCode: feature.fipsCode || feature.fips,
  fips: feature.fips || feature.fipsCode,
  extent: feature.extent,
  state: feature.state,
  path: feature.path,
});
export const createPlaceBreadcrumb = (feature = {}) => ({
  id: feature.geoid,
  type: 'place',
  placeId: feature.id,
  text: feature.title || feature.text || feature.name,
  place_type: feature.place_type,
  geoid: feature.geoid,
  fipsCode: feature.fipsCode || feature.fips,
  fips: feature.fips || feature.fipsCode,
  extent: feature.extent,
  state: feature.state,
  path: feature.path,
  geometry: feature.gisData?.geom || null,
});

const getStateLabel = (stateAbbr = '') =>
  states.find(state => state.abbreviation === (stateAbbr || '').toUpperCase())?.name || (stateAbbr || '').toUpperCase();

const normalizeGAPIPlace = (feature = {}) => {
  const placeTypeRaw = Array.isArray(feature.placeType) ? feature.placeType[0] : feature.placeType;
  const placeType = placeTypeRaw || feature.place_type;
  const placeTypeLower = (placeType || '').toString().toLowerCase();
  const stateFromId = feature.id
    ?.toString()
    .split('-')?.[0]
    ?.toUpperCase();
  const normalizedPath = (feature.url || feature.path || '').toString().replace(/^\/+/, '');
  let normalizedPlaceType = placeType;
  if (placeTypeLower === 'state') {
    normalizedPlaceType = 'State';
  }
  else if (placeTypeLower === 'county') {
    normalizedPlaceType = 'County';
  }
  return {
    ...feature,
    place_type: normalizedPlaceType,
    path: normalizedPath,
    title: (Array.isArray(feature.title) ? feature.title[0] : feature.title) || feature.text || feature.name,
    state: (feature.state || stateFromId || '').toString().toUpperCase(),
  };
};

const ADD_LAYER_GRID_IDLE_TIMEOUT_MS = 8000;

const resetPlaceLayerGrid = (map) => {
  if (hasLayer(map, 'place_state')) {
    map.setPaintProperty('place_state', 'fill-opacity', 0);
    map.setLayoutProperty('place_state', 'visibility', 'none');
    map.setFilter('place_state', ['all', ['==', 'place_type', 'County']]);
    window.blockPlaceClick = false;
  }
};

export const addLayerGrid = map =>
  new Promise((res) => {
    let settled = false;
    const resolveOnce = (value) => {
      if (!settled) {
        settled = true;
        res(value);
      }
    };
    window.blockPlaceClick = true;
    const timeoutId = setTimeout(() => {
      resolveOnce({target: map});
    }, ADD_LAYER_GRID_IDLE_TIMEOUT_MS);
    if (!hasLayer(map, 'place_state')) {
      resolveOnce({target: map});
      return;
    }
    map.setFilter('place_state', ['all', ['in', 'place_type', ...placeTypes.County, 'County', 'State']]);
    map.once('idle', () => {
      clearTimeout(timeoutId);
      if (settled) {
        resetPlaceLayerGrid(map);
        return;
      }
      map.setPaintProperty('place_state', 'fill-opacity', 0);
      map.setLayoutProperty('place_state', 'visibility', 'visible');
      map.once('idle', (e) => {
        if (settled) {
          resetPlaceLayerGrid(map);
          return;
        }
        resolveOnce(e);
      });
    });
  });

export const hideLayerGrid = map =>
  new Promise((res, rej) => {
    if (hasLayer(map, 'place_state')) {
      map.setPaintProperty('place_state', 'fill-opacity', 0);
      map.setLayoutProperty('place_state', 'visibility', 'none');
      map.once('idle', () => {
        map.setFilter('place_state', ['all', ['==', 'place_type', 'County']]);
        window.blockPlaceClick = false;
        res();
      });
    }
    else {
      rej(new Error(`Missing ${map ? 'map' : 'layer'}`));
    }
  });

const loadByPlaceId = (map, placesParamArr = []) => {
  const features = map.queryRenderedFeatures({
    layers: ['place_state'],
    filter: [
      'all',
      ['in', 'place_type', ...placeTypes.County],
      ['in', 'id', ...placesParamArr],
      ['in', 'import_feed', 'census.gov', 'usa_neighborhoods'],
    ],
  });
  const importFeedRank = (feed) => {
    if (feed === 'census.gov') return 0;
    if (feed === 'usa_neighborhoods') return 1;
    return 2;
  };
  const censusFirst = (a, b) => importFeedRank(a.properties?.import_feed) - importFeedRank(b.properties?.import_feed);
  const sorted = [...features].sort(censusFirst);
  return sorted.map(place => place.properties)[0];
};

const isValidFilterValue = v => typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';

const getStateAndCounty = (map, searchFeatureData) => {
  const result = {};
  if (isValidFilterValue(searchFeatureData?.state)) {
    result.state = map
      .queryRenderedFeatures({
        layers: ['place_state'],
        filter: ['all', ['==', 'place_type', 'State'], ['==', 'state', searchFeatureData.state]],
      })
      .map(place => place.properties)[0];
  }
  const countyGeoid = searchFeatureData?.fips ?? searchFeatureData?.fipsCode;
  if (isValidFilterValue(countyGeoid)) {
    result.county = map
      .queryRenderedFeatures({
        layers: ['place_state'],
        filter: ['all', ['in', 'place_type', 'County'], ['==', 'geoid', countyGeoid]],
      })
      .map(place => place.properties)[0];
  }
  return result;
};

const BREADCRUMB_SAFETY_TIMEOUT_MS = 12000;
const BREADCRUMB_PLACE_LOOKUP_RETRIES = 30;
const BREADCRUMB_PLACE_LOOKUP_INTERVAL_MS = 100;

const clearBreadcrumbBlocking = (setBreadcrumbs, map) => {
  if (map) {
    resetPlaceLayerGrid(map);
  }
  else {
    window.blockPlaceClick = false;
  }
  setBreadcrumbs([], true);
};

const fetchBreadcrumbsData = (map, setBreadcrumbs, options) => {
  let safetyTimeoutId;
  const clearSafetyTimeout = () => {
    if (safetyTimeoutId) {
      clearTimeout(safetyTimeoutId);
      safetyTimeoutId = null;
    }
  };
  const done = (breadcrumbsArray, stopLoading) => {
    clearSafetyTimeout();
    setBreadcrumbs(breadcrumbsArray, stopLoading);
    if (stopLoading) {
      window.blockPlaceClick = false;
    }
  };

  safetyTimeoutId = setTimeout(() => {
    clearBreadcrumbBlocking(setBreadcrumbs, map);
  }, BREADCRUMB_SAFETY_TIMEOUT_MS);

  addLayerGrid(map)
    .then((e) => {
      const {placesParamArr} = options;
      let triesIterator = 0;
      const getData = () => {
        const breadcrumbsArray = [];
        const searchFeatureData = loadByPlaceId(e.target, placesParamArr);

        if (searchFeatureData) {
          const resultData = getStateAndCounty(e.target, searchFeatureData);
          if (resultData.state) {
            breadcrumbsArray.push(createStateBreadcrumb(resultData.state));
          }
          if (resultData.county) {
            breadcrumbsArray.push(createCountyBreadcrumb(resultData.county));
          }
          breadcrumbsArray.push(createPlaceBreadcrumb(searchFeatureData));
          hideLayerGrid(e.target)
            .then(() => {
              done(breadcrumbsArray, true);
            })
            .catch(() => {
              window.blockPlaceClick = false;
              done(breadcrumbsArray, true);
            });
        }
        else {
          triesIterator += 1;
          if (triesIterator < BREADCRUMB_PLACE_LOOKUP_RETRIES) {
            setTimeout(() => {
              getData();
            }, BREADCRUMB_PLACE_LOOKUP_INTERVAL_MS);
          }
          else {
            hideLayerGrid(e.target)
              .then(() => {
                done([], true);
              })
              .catch(() => {
                clearSafetyTimeout();
                clearBreadcrumbBlocking(setBreadcrumbs, e.target);
              });
          }
        }
      };
      getData();
    })
    .catch(() => {
      clearSafetyTimeout();
      clearBreadcrumbBlocking(setBreadcrumbs, map);
    });
};

export const getPointBreadcrumbsData = (
  map,
  setBreadcrumbs,
  options = {
    placesParamArr: [],
    gridInitialized: false,
    setInitialBreadcrumb: true,
  },
) => {
  const {searchPlaceId, gridInitialized, setInitialBreadcrumb} = options;
  if (searchPlaceId && setInitialBreadcrumb) {
    const selectedState = searchPlaceId
      .toString()
      .split('-')?.[0]
      ?.toUpperCase();
    setBreadcrumbs(
      [
        createStateBreadcrumb({
          type: 'state',
          title: getStateLabel(selectedState),
          place_type: 'State',
          state: selectedState,
        }),
      ],
      false,
    );
  }

  const loadBreadcrumbs = () => {
    const mapInstance = map || window.mapInstance;
    if (!mapInstance) {
      setBreadcrumbs([]);
      console.warn('Map loading issues');
    }
    if (mapInstance.isStyleLoaded()) {
      fetchBreadcrumbsData(map, setBreadcrumbs, options);
    }
    else {
      mapInstance.once('idle', (e) => {
        fetchBreadcrumbsData(e.target, setBreadcrumbs, options);
      });
    }
  };
  if (gridInitialized) {
    loadBreadcrumbs();
  }
  else {
    window.gridInitializeCallback = loadBreadcrumbs;
  }
};

export const addPlaceStateSubQuery = url => url;
export const addPlaceCountySubQuery = url => url;

export const getPropertySearch = async () => {
  const token = await getActualPublicToken();
  return new UriJs(`${BASE_URL_TYPE.API}/api/ui-api`)
    .search('action=_property_record&featureLayer=state')
    .addSearch('type', '_property_record')
    .addSearch('fields[]', 'id')
    .addSearch('fields[]', 'title')
    .addSearch('fields[]', 'folio')
    .addSearch('fields[]', 'address')
    .addSearch('fields[]', 'state')
    .addSearch('fields[]', 'fipsCode')
    .addSearch('fields[]', 'placeType')
    .addSearch('fields[]', 'url')
    .addSearch('fields[]', 'place')
    .addSearch('geometryFormat', 'none')
    .addSearch('state_env', utilities.getCurrentStateEnv())
    .addSearch('publicToken', token);
};

export const getPlaceSearch = async () => {
  const token = await getActualPublicToken();
  return new UriJs(`${BASE_URL_TYPE.API}/api/ui-api`)
    .search('?action=_place&featureLayer=state')
    .addSearch('type', '_place')
    .addSearch('fields[]', 'id')
    .addSearch('fields[]', 'title')
    .addSearch('fields[]', 'extent')
    .addSearch('fields[]', 'fipsCode')
    .addSearch('fields[]', 'placeType')
    .addSearch('fields[]', 'geoid')
    .addSearch('fields[]', 'url')
    .addSearch('geometryFormat', 'json')
    .addSearch('importFeed', 'census.gov,usa_neighborhoods')
    .addSearch('state_env', utilities.getCurrentStateEnv())
    .addSearch('publicToken', token);
};

export const getPlaceAutocompleteSearch = async () => {
  const token = await getActualPublicToken();
  return new UriJs(`${BASE_URL_TYPE.API}/fast-ajax/autocomplete`)
    .addSearch('featureLayer', 'state')
    .addSearch('field', 'place')
    .addSearch('importFeed', 'census.gov,usa_neighborhoods')
    .addSearch('placeType', `${ALLOWED_PLACE_TYPES.join(',')},unincorporated,state,county`)
    .addSearch('example', 1)
    .addSearch('state_env', utilities.getCurrentStateEnv())
    .addSearch('publicToken', token);
};

export const getSearchResults = (url, fullArray = false) =>
  axios(url.normalizeSearch().readable()).then(({data}) => {
    if (url.hasSearch('example')) {
      return data;
    }
    if (!data.items || !data.items.length) {
      return null;
    }
    return fullArray ? data.items : data.items[0];
  });

const getPlaceData = async (search = [], options = {}, fullArray = false) => {
  const url = await getPlaceSearch();
  if (options.stateSubQuery) {
    addPlaceStateSubQuery(url);
  }
  if (options.countySubQuery) {
    addPlaceStateSubQuery(url);
  }
  search.forEach((item) => {
    url.addSearch(item.param, item.value);
  });
  if (options.onlyMarkets) {
    url.addSearch('placeType', `${ALLOWED_PLACE_TYPES.join(',')},unincorporated`);
    return getSearchResults(url, fullArray);
  }

  // eslint-disable-next-line consistent-return
  return getSearchResults(url, fullArray);
};

export const getPlaceById = (id, options, fullArray = false) =>
  getPlaceData([{value: id, param: 'id'}], options, fullArray);

export const getPlaceByPoint = (e, options) =>
  getPlaceData(
    [
      {
        value: `{"geometry":"POINT(${e.lngLat.lng} ${e.lngLat.lat})"}`,
        param: 'point_search',
      },
    ],
    options,
  );
export const getPlaceByPath = (path, options) => getPlaceData([{value: path, param: 'url'}], options);

export const fetchBreadcrumbsFromGAPI = async (placeData) => {
  if (!placeData) {
    return [];
  }

  const normalizedPlace = normalizeGAPIPlace(placeData);
  const stateAbbr = normalizedPlace.state
    || normalizedPlace.placeId
      ?.toString()
      .split('-')?.[0]
      ?.toUpperCase()
    || normalizedPlace.id
      ?.toString()
      .split('-')?.[0]
      ?.toUpperCase();
  const countyGeoid = normalizedPlace.fipsCode || normalizedPlace.fips;
  const stateCodeSource = normalizedPlace.fipsCode || normalizedPlace.geoid || normalizedPlace.fips || '';
  const stateFipsCode = stateCodeSource.toString().slice(0, 2);
  const stateId = stateAbbr && stateFipsCode ? `${stateAbbr.toLowerCase()}-state-place-${stateFipsCode}` : '';
  const [stateData, countyData] = await Promise.all([
    stateId ? getPlaceData([{value: stateId, param: 'id'}], {}, false) : Promise.resolve(null),
    countyGeoid
      ? getPlaceData(
        [
          {value: 'county', param: 'placeType'},
          {value: countyGeoid, param: 'geoid'},
        ],
        {},
        false,
      )
      : Promise.resolve(null),
  ]);
  const normalizedCounty = countyData ? normalizeGAPIPlace(countyData) : null;
  const fallbackStateAbbr = stateAbbr || normalizedCounty?.state;
  const fallbackStatePath = fallbackStateAbbr ? `state/us/${fallbackStateAbbr.toLowerCase()}` : '';
  const fallbackStateId = stateId || (fallbackStateAbbr ? `${fallbackStateAbbr.toLowerCase()}-state-place` : '');

  const breadcrumbs = [];
  if (stateData) {
    breadcrumbs.push(createStateBreadcrumb(normalizeGAPIPlace(stateData)));
  }
  else if (fallbackStateAbbr) {
    breadcrumbs.push(
      createStateBreadcrumb({
        id: stateFipsCode || fallbackStateAbbr,
        geoid: stateFipsCode || fallbackStateAbbr,
        placeId: fallbackStateId,
        title: getStateLabel(fallbackStateAbbr),
        place_type: 'State',
        state: fallbackStateAbbr,
        path: fallbackStatePath,
      }),
    );
  }
  if (normalizedCounty) {
    breadcrumbs.push(createCountyBreadcrumb(normalizedCounty));
  }
  breadcrumbs.push(createPlaceBreadcrumb(normalizeGAPIPlace(placeData)));
  return breadcrumbs;
};

export const fitPlaceBounds = (bounds, map = null, leftPanel = true, fitKey = '') => {
  if (shouldSkipNavigationFit(fitKey)) {
    return;
  }
  const mapInstance = map || window.mapInstance;
  fitBounds(
    mapInstance,
    typeof bounds === 'string' ? bounds.split(',').map(i => +i) : bounds,
    leftPanel ? leftPanelWidth() : 20,
  );
};

export const initialLoadProperty = () => {
  // TODO: load property
};

// eslint-disable-next-line no-underscore-dangle
export const __test__ = {
  getNavigationFitKey,
  shouldSkipNavigationFit,
  resetNavigationFit,
};
